/**
 * A connector field's numeric `min` and `max` rules (seed `validation`), one rule for the form and
 * the server (round 7, Mouseflow v1.2.0 Parallel requests, 1 to 8).
 *
 * The form's `useValidation` always held a value to them; the server held nothing, so an API
 * caller could store a number the image then quietly clamps while the connection page keeps
 * showing the stored one. Same reading on both sides: an empty value is not checked (an optional
 * field's blank means the image's default), a value that is not a number is left to the image
 * (the form's number control cannot produce one), and a number outside a bound answers the seed's
 * own message.
 */

import { localDateOf } from "../cron/cronUtils";

export interface RangeRules {
  min?: { value: number; message: string } | null;
  max?: { value: number; message: string } | null;
}

/** The seed's message for a value outside the field's range, or null. */
export function rangeIssue(rules: RangeRules | null | undefined, value: unknown): string | null {
  if (!rules || value === undefined || value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  if (typeof value !== "string" && typeof value !== "number") return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  if (rules.min != null && numeric < rules.min.value) return rules.min.message;
  if (rules.max != null && numeric > rules.max.value) return rules.max.message;
  return null;
}

/** The latest calendar day anywhere on earth right now (UTC+14), as YYYY-MM-DD. */
export function latestTodayAnywhere(now: Date = new Date()): string {
  return new Date(now.getTime() + 14 * 3_600_000).toISOString().slice(0, 10);
}

/** The zone a person without one of their own reads "today" in (owner 10/15: never UTC). */
export const FALLBACK_DAY_ZONE = "America/Los_Angeles";

/** Today's calendar day in `zone` (else Pacific, for a zone this runtime cannot read), YYYY-MM-DD. */
export function todayIn(zone: string, now: Date = new Date()): string {
  try {
    return localDateOf(now, zone);
  } catch {
    return localDateOf(now, FALLBACK_DAY_ZONE);
  }
}

/**
 * The seed's message when a YYYY-MM-DD day is later than today (#1094: an import from a future
 * day reads nothing). With `zone` (the form: the person's display zone, `useDisplayTz`) "today"
 * is that zone's day, so the field refuses tomorrow as the person reads it. Without one (the
 * server, which has no reader's zone) it is the day in the zone furthest ahead, so a day that is
 * today anywhere still passes there and the server refuses only a day later than today
 * everywhere. A value that is not a YYYY-MM-DD day is left to the pattern rule.
 */
export function notAfterTodayIssue(
  rule: { value: boolean; message: string } | null | undefined,
  text: string,
  now: Date = new Date(),
  zone?: string,
): string | null {
  if (!rule || rule.value !== true) return null;
  const day = text.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const today = zone ? todayIn(zone, now) : latestTodayAnywhere(now);
  return day > today ? rule.message : null;
}

/**
 * Whether one seed dependency's condition holds for the source field's value, or null for an
 * operator this build does not know (the caller leaves the field as declared). The form's
 * `useFieldDependencies` and the server's range check read the same rule.
 */
export function dependencyHolds(
  operator: unknown,
  expected: unknown,
  sourceValue: unknown,
): boolean | null {
  switch (operator) {
    case "equals":
      return String(sourceValue) === String(expected);
    case "notEquals":
      return String(sourceValue) !== String(expected);
    case "in":
      return Array.isArray(expected) && expected.includes(String(sourceValue));
    case "notIn":
      return Array.isArray(expected) && !expected.includes(String(sourceValue));
    case "isNotEmpty":
      return sourceValue !== "" && sourceValue !== null && sourceValue !== undefined;
    case "isEmpty":
      return sourceValue === "" || sourceValue === null || sourceValue === undefined;
    default:
      return null;
  }
}

/**
 * Whether a field is visible for the submitted values: its declared `visible` (true when absent),
 * then each dependency whose condition holds, in order, with `show` and `hide` effects; anything
 * else is left as declared. The form skips a hidden field's rules but still submits its value, so
 * the server's range check skips it too (round 7 review).
 */
export function isFieldVisible(field: unknown, values: Readonly<Record<string, unknown>>): boolean {
  if (!field || typeof field !== "object") return true;
  const { visible, dependencies } = field as { visible?: unknown; dependencies?: unknown };
  let shown = visible !== false;
  if (!Array.isArray(dependencies)) return shown;
  for (const dependency of dependencies) {
    if (!dependency || typeof dependency !== "object") continue;
    const { sourceFieldId, operator, value, effect } = dependency as {
      sourceFieldId?: unknown;
      operator?: unknown;
      value?: unknown;
      effect?: { type?: unknown } | null;
    };
    if (typeof sourceFieldId !== "string") continue;
    if (dependencyHolds(operator, value, values[sourceFieldId]) !== true) continue;
    if (effect?.type === "show") shown = true;
    else if (effect?.type === "hide") shown = false;
  }
  return shown;
}
